export type ShiftPeriod = 'All' | 'Early' | 'Late' | 'Night';

export interface ShiftTimeRange {
    startTime: Date;
    endTime: Date;
}

// Define shift hours [startHour, endHour]
const shiftHours: Record<
    Exclude<ShiftPeriod, 'All'>,
    readonly [number, number]
> = {
    Early: [6, 14],
    Late: [14, 22],
    Night: [22, 6], // Crosses midnight
} as const;

export const getShiftTimeRange = (shift: ShiftPeriod): ShiftTimeRange => {
    const now = new Date();

    // For "All", return the last 24 hours
    if (shift === 'All') {
        const startTime = new Date(now);
        startTime.setHours(
            now.getHours() - 24,
            now.getMinutes(),
            now.getSeconds(),
            now.getMilliseconds()
        );
        return {
            startTime,
            endTime: now,
        };
    }

    const [startHour, endHour] = shiftHours[shift];
    const currentHour = now.getHours();

    // Create potential shift times for today
    const todayStart = new Date(now);
    todayStart.setHours(startHour, 0, 0, 0);

    const todayEnd = new Date(now);
    todayEnd.setHours(endHour, 0, 0, 0);

    // Handle night shift (crosses midnight)
    if (shift === 'Night') {
        // If we're currently before 6:00 AM (in the night shift that started yesterday)
        if (currentHour < endHour) {
            const yesterdayStart = new Date(todayStart);
            yesterdayStart.setDate(yesterdayStart.getDate() - 1);
            yesterdayStart.setHours(startHour, 0, 0, 0);
            return {
                startTime: yesterdayStart,
                endTime: now,
            };
        }

        // If we're currently at or after 22:00 (in tonight's shift)
        if (currentHour >= startHour) {
            return {
                startTime: todayStart,
                endTime: now,
            };
        }

        // Otherwise, we're between 6:00 and 22:00, return the last completed night shift
        const yesterdayStart = new Date(todayStart);
        yesterdayStart.setDate(yesterdayStart.getDate() - 1);
        yesterdayStart.setHours(startHour, 0, 0, 0);
        return {
            startTime: yesterdayStart,
            endTime: todayEnd,
        };
    }

    // For Early and Late shifts (don't cross midnight)
    // If we're currently in the shift
    if (currentHour >= startHour && currentHour < endHour) {
        return {
            startTime: todayStart,
            endTime: now,
        };
    }

    // If we're after the shift ended today
    if (currentHour >= endHour) {
        return {
            startTime: todayStart,
            endTime: todayEnd,
        };
    }

    // If we're before the shift starts today, use yesterday's shift
    const yesterdayStart = new Date(todayStart);
    yesterdayStart.setDate(yesterdayStart.getDate() - 1);

    const yesterdayEnd = new Date(todayEnd);
    yesterdayEnd.setDate(yesterdayEnd.getDate() - 1);

    return {
        startTime: yesterdayStart,
        endTime: yesterdayEnd,
    };
};

export const getPreviousShift = (): ShiftPeriod => {
    const now = new Date();
    const hour = now.getHours();

    // Determine which shift we're currently in, then return the one before it
    if (hour >= shiftHours.Early[0] && hour < shiftHours.Early[1]) {
        return 'Night';
    } else if (hour >= shiftHours.Late[0] && hour < shiftHours.Late[1]) {
        return 'Early';
    } else {
        return 'Late';
    }
};

export const formatShiftTimeRangeForApi = (range: ShiftTimeRange) => {
    return {
        startTime: range.startTime.toISOString(),
        endTime: range.endTime.toISOString(),
    };
};
